/**
 * Pisos comerciais — um por linha de mensalidade.
 *
 * A primeira versão pedia um piso por curso, e isso não funcionou: o mesmo
 * curso tem mais de um preço de tabela (Fundamental II com bilíngue custa
 * 1.961,31; sem bilíngue, menos). Com piso único por curso, a proposta da linha
 * bilíngue era comparada com o piso da linha sem bilíngue e reprovava mesmo
 * estando correta.
 *
 * Por isso esta tela **não é uma lista de pisos**: é a lista das mensalidades
 * cadastradas, cada uma com o seu piso ao lado. Assim é impossível cadastrar um
 * piso para algo que não existe, ou deixar uma variante de fora sem perceber —
 * a linha sem piso fica visível, marcada como sem bloqueio.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { apagar, del, get, patch, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import { moeda } from "../lib/formato";
import { Confirmacao, Modal } from "../components/Modal";
import { Cabecalho } from "../components/Layout";
import { periodoDaLinha, ROTULO_PERIODO_VALOR } from "../lib/periodoValor";
import type { TabelaValor } from "../lib/tipos";

const VERDE = "#0e8c3a";

interface Rascunho {
  /** id do piso, quando já existe. */
  id?: string;
  mensalidade: TabelaValor;
  valor: string;
}

export default function ValoresMinimos() {
  const { podeAoMenos } = useAuth();
  const podeEditar = podeAoMenos("GESTOR");
  // apagar é irreversível: fica com quem responde pelo cadastro
  const podeApagar = podeAoMenos("ADMIN");

  const [mensalidades, setMensalidades] = useState<TabelaValor[]>([]);
  const [pisos, setPisos] = useState<TabelaValor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [editando, setEditando] = useState<Rascunho | null>(null);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState<TabelaValor | null>(null);
  const [apagandoMensalidade, setApagandoMensalidade] = useState<TabelaValor | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    return Promise.all([
      get<TabelaValor[]>("/api/tabelas/valores?tipo=MENSALIDADE"),
      get<TabelaValor[]>("/api/tabelas/valores?tipo=MINIMO"),
    ])
      .then(([m, p]) => { setMensalidades(m); setPisos(p); setErro(""); })
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const pisoDe = useCallback(
    (mensalidadeId: string) => pisos.find((p) => p.referenciaId === mensalidadeId),
    [pisos],
  );

  /**
   * Pisos do formato antigo, cadastrados por curso antes desta mudança.
   * Não valem mais nada — a conferência só olha piso com referência — mas
   * ficam à vista para o gestor apagar, em vez de sumirem em silêncio e
   * deixarem a impressão de que o bloqueio está configurado.
   */
  const orfaos = useMemo(() => pisos.filter((p) => !p.referenciaId), [pisos]);
  const semPiso = mensalidades.filter((m) => !pisoDe(m.id)).length;

  /**
   * Linhas idênticas no cadastro de mensalidade — mesma descrição, mesmo valor,
   * mesmo curso. Acontecem por cadastro repetido e são traiçoeiras aqui: a
   * pessoa define o piso numa delas, vê "sem bloqueio" na outra e não entende
   * qual está valendo. Marcar é melhor que esconder, porque só o gestor sabe
   * qual das duas deve ficar.
   */
  const duplicadas = useMemo(() => {
    const conta = new Map<string, number>();
    const chave = (m: TabelaValor) =>
      `${m.descricao.trim().toLowerCase()}|${Number(m.valor)}|${m.curso?.id ?? ""}`;
    for (const m of mensalidades) conta.set(chave(m), (conta.get(chave(m)) ?? 0) + 1);
    return (m: TabelaValor) => (conta.get(chave(m)) ?? 0) > 1;
  }, [mensalidades]);

  async function salvar() {
    if (!editando) return;
    setErroForm("");
    const valor = Number(editando.valor.replace(",", "."));
    if (editando.valor.trim() === "") return setErroForm("Informe o valor mínimo (R$).");
    if (!Number.isFinite(valor) || valor <= 0) return setErroForm("Valor inválido.");

    const cheio = Number(editando.mensalidade.valor);
    if (Number.isFinite(cheio) && valor > cheio) {
      return setErroForm(
        `O mínimo não pode passar da própria mensalidade (${moeda(cheio)}) — nenhuma proposta conseguiria ser salva.`,
      );
    }

    setSalvando(true);
    try {
      const corpo = {
        tipo: "MINIMO",
        descricao: `Mínimo — ${editando.mensalidade.descricao}`,
        valor,
        cursoId: editando.mensalidade.curso?.id ?? null,
        referenciaId: editando.mensalidade.id,
      };
      if (editando.id) await patch(`/api/tabelas/valores/${editando.id}`, corpo);
      else await post("/api/tabelas/valores", corpo);
      setEditando(null);
      await carregar();
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!removendo) return;
    try {
      // piso é cadastro de política, não histórico: sai de vez, sem desativado
      await apagar(`/api/tabelas/valores/${removendo.id}`);
      setRemovendo(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao remover");
      setRemovendo(null);
    }
  }

  async function apagarMensalidade() {
    if (!apagandoMensalidade) return;
    try {
      await apagar(`/api/tabelas/valores/${apagandoMensalidade.id}`);
      setApagandoMensalidade(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao apagar");
      setApagandoMensalidade(null);
    }
  }

  return (
    <>
      <Cabecalho titulo="Valores Mínimos" />

      <p style={{ fontSize: 13, color: "var(--texto-suave)", margin: "0 0 16px", lineHeight: 1.6 }}>
        Piso de cada mensalidade depois da condição exclusiva. A lista abaixo é o próprio cadastro de{" "}
        <strong>Valores Mensalidade</strong> — cada variante tem o seu piso, porque o mesmo curso tem
        preços diferentes com e sem bilíngue. Mensalidade <strong>sem piso não é bloqueada</strong>.
      </p>

      {semPiso > 0 && (
        <div style={{ background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "#b54708", lineHeight: 1.6 }}>
          <strong>{semPiso}</strong> {semPiso === 1 ? "mensalidade está" : "mensalidades estão"} sem piso
          cadastrado. Nessas, qualquer desconto passa.
        </div>
      )}

      {orfaos.length > 0 && (
        <div style={{ background: "#fef3f2", border: "1px solid #fda29b", borderRadius: 10, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: "#b42318", lineHeight: 1.6 }}>
          <strong>{orfaos.length}</strong> {orfaos.length === 1 ? "piso antigo" : "pisos antigos"}, cadastrados
          por curso, {orfaos.length === 1 ? "não vale" : "não valem"} mais — o piso agora é por mensalidade.
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 8 }}>
            {orfaos.map((o) => (
              <button key={o.id} className="botao secundario"
                style={{ padding: "4px 10px", fontSize: 12, color: "#b42318" }}
                onClick={() => setRemovendo(o)}>
                Apagar "{o.descricao}" ({moeda(o.valor)})
              </button>
            ))}
          </div>
        </div>
      )}

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      <div className="cartao" style={{ padding: 0, overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Mensalidade (cadastro de valores)</th>
              <th style={{ width: 150 }}>Valor cheio</th>
              <th style={{ width: 200 }}>Curso</th>
              <th style={{ width: 170 }}>Mínimo</th>
              <th style={{ width: 190 }}></th>
            </tr>
          </thead>
          <tbody>
            {mensalidades.map((m) => {
              const piso = pisoDe(m.id);
              return (
                <tr key={m.id}>
                  <td>
                    {m.descricao}
                    {(() => {
                      const p = periodoDaLinha(m);
                      return (
                        <span
                          title={p ? `Aparece no bloco ${p === "PARCIAL" ? "4A (parcial)" : "4B (integral)"}` : "Sem período: aparece nos dois blocos"}
                          style={{
                            fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "1px 5px",
                            marginLeft: 6, verticalAlign: 1,
                            background: p ? "#f9fafb" : "#fffaeb",
                            color: p ? "#667085" : "#b54708",
                            border: `1px solid ${p ? "#eaecf0" : "#fedf89"}`,
                          }}
                        >
                          {p ? ROTULO_PERIODO_VALOR[p].toUpperCase() : "SEM PERÍODO"}
                        </span>
                      );
                    })()}
                    {duplicadas(m) && (
                      <span
                        title="Existe outra linha idêntica no cadastro de mensalidade. Apague a que sobra para não ficar dúvida sobre qual piso está valendo."
                        style={{ fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "1px 5px", marginLeft: 6, background: "#fffaeb", color: "#b54708", border: "1px solid #fedf89", verticalAlign: 1 }}
                      >
                        DUPLICADA
                      </span>
                    )}
                  </td>
                  <td><strong>{moeda(m.valor)}</strong></td>
                  <td style={{ color: "var(--texto-suave)" }}>{m.curso?.nome ?? "Todos os cursos"}</td>
                  <td>
                    {piso ? (
                      <strong style={{ color: VERDE }}>{moeda(piso.valor)}</strong>
                    ) : (
                      <span style={{ fontSize: 12, color: "#b54708" }}>sem bloqueio</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {podeEditar && (
                      <>
                        <button className="botao secundario" style={{ padding: "5px 10px", fontSize: 12 }}
                          onClick={() => {
                            setErroForm("");
                            setEditando({ id: piso?.id, mensalidade: m, valor: piso ? String(piso.valor) : "" });
                          }}>
                          {piso ? "Editar" : "Definir"}
                        </button>
                        {piso && (
                          <button className="botao secundario"
                            style={{ padding: "5px 10px", fontSize: 12, marginLeft: 6, color: "#b42318" }}
                            onClick={() => setRemovendo(piso)}>
                            Remover
                          </button>
                        )}
                        {podeApagar && (
                          <button
                            onClick={() => setApagandoMensalidade(m)}
                            title="Apagar esta linha do cadastro de mensalidade"
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: 4, marginLeft: 4, opacity: .55 }}
                            onMouseEnter={(ev) => { ev.currentTarget.style.opacity = "1"; }}
                            onMouseLeave={(ev) => { ev.currentTarget.style.opacity = ".55"; }}
                          >
                            🗑️
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!carregando && mensalidades.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32 }}>
                Nenhuma mensalidade cadastrada. Cadastre em "Valores Mensalidade" primeiro.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editando && (
        <Modal
          titulo={editando.id ? "Editar mínimo" : "Definir mínimo"}
          largura={520}
          onFechar={() => setEditando(null)}
          rodape={
            <>
              <button className="botao secundario" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</button>
              <button className="botao" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</button>
            </>
          }
        >
          {erroForm && <div className="erro" style={{ marginBottom: 12 }}>{erroForm}</div>}

          <div style={{ background: "#f9fafb", border: "1px solid #eaecf0", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600 }}>{editando.mensalidade.descricao}</div>
            <div style={{ color: "var(--texto-suave)" }}>
              Valor cheio {moeda(editando.mensalidade.valor)}
              {editando.mensalidade.curso ? ` · ${editando.mensalidade.curso.nome}` : ""}
            </div>
          </div>

          <label className="rotulo">Valor mínimo com condição exclusiva (R$)</label>
          <input
            className="campo" type="number" step="0.01" min="0" autoFocus
            placeholder="Ex.: 1176.79"
            value={editando.valor}
            onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
          />
          <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 6, lineHeight: 1.6 }}>
            Proposta que ficar abaixo deste valor é recusada, e o consultor não consegue salvar.
          </div>
        </Modal>
      )}

      {removendo && (
        <Confirmacao
          titulo="Remover mínimo"
          perigo
          rotuloConfirmar="Remover"
          mensagem={
            <>
              Remover o mínimo de <strong>{moeda(removendo.valor)}</strong>?
              <div style={{ marginTop: 8, color: "var(--texto-suave)", fontSize: 13 }}>
                Essa mensalidade passa a aceitar qualquer desconto, sem bloqueio.
              </div>
            </>
          }
          onConfirmar={remover}
          onCancelar={() => setRemovendo(null)}
        />
      )}

      {apagandoMensalidade && (
        <Confirmacao
          titulo="Apagar esta linha de mensalidade?"
          perigo
          rotuloConfirmar="Apagar"
          mensagem={
            <>
              <strong>{apagandoMensalidade.descricao}</strong> — {moeda(apagandoMensalidade.valor)}
              {apagandoMensalidade.curso ? ` · ${apagandoMensalidade.curso.nome}` : ""}
              <div style={{ marginTop: 10, color: "var(--texto-suave)", fontSize: 13, lineHeight: 1.6 }}>
                Some da lista de opções do formulário e leva junto o mínimo desta linha.
                <br />
                <strong>As visitas e propostas já feitas não mudam</strong> — elas guardam uma cópia
                do valor, não um vínculo com esta tabela.
              </div>
              {pisoDe(apagandoMensalidade.id) && (
                <div style={{ marginTop: 8, color: "#b42318", fontSize: 13 }}>
                  O mínimo de {moeda(pisoDe(apagandoMensalidade.id)!.valor)} vai junto.
                </div>
              )}
              <div style={{ marginTop: 10, fontSize: 13, color: "#b42318" }}>Não há como desfazer.</div>
            </>
          }
          onConfirmar={apagarMensalidade}
          onCancelar={() => setApagandoMensalidade(null)}
        />
      )}
    </>
  );
}
